import type { ColumnDef } from "@tanstack/react-table";

import type { Inserat } from "@/api/inserate";
import {
  EM_DASH,
  formatEur,
  formatEurPerM2,
  formatM2,
  formatRelative,
  formatScore,
  pricePerM2,
} from "@/lib/format";
import { AnalyseCell } from "./AnalyseCell";
import { StatusCell } from "./StatusCell";

/** Column definitions for the Inserate triage table. Pure data — no callbacks
 * here yet (status mutation + analyse trigger land in 5d via a wrapper). */
export const inserateColumns: ColumnDef<Inserat>[] = [
  {
    id: "quelle",
    header: "Quelle",
    accessorKey: "quelle",
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.quelle}</span>
    ),
  },
  {
    id: "stadt",
    header: "Stadt",
    accessorFn: (row) => row.adresse.ort ?? "",
    cell: ({ getValue }) => {
      const v = getValue<string>();
      return v || EM_DASH;
    },
  },
  {
    id: "kaufpreis_eur",
    header: "Preis",
    accessorKey: "kaufpreis_eur",
    cell: ({ row }) => (
      <span className="tabular-nums">
        {formatEur(row.original.kaufpreis_eur)}
      </span>
    ),
  },
  {
    id: "wohnflaeche_m2",
    header: "Wohnfläche",
    accessorKey: "wohnflaeche_m2",
    cell: ({ row }) => (
      <span className="tabular-nums">
        {formatM2(row.original.wohnflaeche_m2)}
      </span>
    ),
  },
  {
    id: "preis_pro_m2",
    header: "€/m²",
    // Numeric accessor so sorting compares numbers, not formatted strings
    accessorFn: (row) =>
      pricePerM2(row.kaufpreis_eur, row.wohnflaeche_m2) ?? Number.NaN,
    sortUndefined: "last",
    cell: ({ row }) => (
      <span className="tabular-nums">
        {formatEurPerM2(
          pricePerM2(row.original.kaufpreis_eur, row.original.wohnflaeche_m2),
        )}
      </span>
    ),
  },
  {
    id: "hard",
    header: "Hard",
    // Sort: pass=true first (descending), null last
    accessorFn: (row) =>
      row.hard_criteria_pass === true
        ? 1
        : row.hard_criteria_pass === false
          ? 0
          : -1,
    cell: ({ row }) => {
      const v = row.original.hard_criteria_pass;
      if (v === true) return <span className="text-green-600">✓</span>;
      if (v === false) return <span className="text-red-600">✗</span>;
      return EM_DASH;
    },
  },
  {
    id: "score",
    header: "Soft",
    accessorKey: "score",
    cell: ({ row }) => (
      <span className="tabular-nums">{formatScore(row.original.score)}</span>
    ),
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "status",
    cell: ({ row }) => <StatusCell inserat={row.original} />,
  },
  {
    id: "letzte_aktualisierung_at",
    header: "Sichtung",
    accessorKey: "letzte_aktualisierung_at",
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {formatRelative(row.original.letzte_aktualisierung_at)}
      </span>
    ),
  },
  {
    id: "aktionen",
    header: "Aktionen",
    enableSorting: false,
    cell: ({ row }) => <AnalyseCell inserat={row.original} />,
  },
];
